/**
 * Public status page. Runs two real checks against the portal backend and
 * reports each result with its latency — nothing here is hardcoded "ok".
 *
 *   • Database: PostgREST read of property_directory_customer_view. Anonymous
 *     sessions get zero rows without an error, which is exactly the
 *     reachability signal we want.
 *   • Authentication: GoTrue health endpoint.
 */
import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { PublicSiteFrame } from '../components/PublicSiteFrame';
import { useI18n } from '../i18n/I18nProvider';

interface CheckResult {
  key: 'database' | 'auth';
  ok: boolean;
  latencyMs: number;
  detail: string | null;
}

async function timed(fn: () => Promise<void>): Promise<{ ok: boolean; latencyMs: number; detail: string | null }> {
  const started = performance.now();
  try {
    await fn();
    return { ok: true, latencyMs: Math.round(performance.now() - started), detail: null };
  } catch (err) {
    return {
      ok: false,
      latencyMs: Math.round(performance.now() - started),
      detail: err instanceof Error ? err.message : String(err),
    };
  }
}

async function runChecks(): Promise<CheckResult[]> {
  const database = await timed(async () => {
    const { error } = await supabase
      .from('property_directory_customer_view')
      .select('code')
      .limit(1);
    if (error) throw new Error(error.message);
  });

  const auth = await timed(async () => {
    const base = import.meta.env.VITE_SUPABASE_URL;
    if (!base) throw new Error('VITE_SUPABASE_URL is not configured');
    const response = await fetch(`${base.replace(/\/$/, '')}/auth/v1/health`, {
      headers: { apikey: import.meta.env.VITE_SUPABASE_ANON_KEY ?? '' },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
  });

  return [
    { key: 'database', ...database },
    { key: 'auth', ...auth },
  ];
}

export function HealthPage() {
  const { t } = useI18n();
  const [results, setResults] = useState<CheckResult[] | null>(null);
  const [checkedAt, setCheckedAt] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  const run = useCallback(async () => {
    setRunning(true);
    const next = await runChecks();
    setResults(next);
    setCheckedAt(new Date().toISOString());
    setRunning(false);
  }, []);

  useEffect(() => {
    void run();
  }, [run]);

  const overallOk = results ? results.every((r) => r.ok) : null;
  const report = {
    status: overallOk === null ? 'checking' : overallOk ? 'ok' : 'degraded',
    portal: 'customer-portal',
    checked_at: checkedAt,
    checks: results ?? [],
  };

  return (
    <PublicSiteFrame>
      <main id="main" className="public-auth-stage">
        <div className="public-auth-card" style={{ maxWidth: '36rem' }}>
          <p className="public-eyebrow" style={{ marginBottom: '1rem' }}>
            <span className="public-eyebrow-rule" aria-hidden />
            {t('health.title')}
          </p>
          <div className="flex items-center justify-between gap-3">
            <h1 className="text-lg font-semibold" style={{ color: '#1F1E26' }}>
              {overallOk === null
                ? t('health.checking')
                : overallOk
                  ? t('health.ok')
                  : t('health.degraded')}
            </h1>
            <span
              aria-hidden="true"
              className="h-3 w-3 rounded-full"
              style={{
                background:
                  overallOk === null ? '#d7d6e2' : overallOk ? '#0f6e56' : '#d44133',
              }}
            />
          </div>
          <p className="mt-1 text-sm" style={{ color: '#6B6A75' }}>
            {t('health.subtitle')}
          </p>

          <ul className="mt-5 divide-y" style={{ borderColor: '#e4e4ec' }} aria-live="polite">
            {(results ?? [{ key: 'database' }, { key: 'auth' }]).map((check) => {
              const full = 'ok' in check ? (check as CheckResult) : null;
              return (
                <li key={check.key} className="flex items-start justify-between gap-3 py-3">
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#1F1E26' }}>
                      {check.key === 'database' ? t('health.database') : t('health.auth')}
                    </p>
                    {full?.detail && (
                      <p className="mt-0.5 text-xs" style={{ color: '#a82c20' }}>
                        {full.detail}
                      </p>
                    )}
                  </div>
                  <div className="text-right text-sm">
                    <p style={{ color: full ? (full.ok ? '#0f6e56' : '#a82c20') : '#6B6A75' }}>
                      {full ? (full.ok ? t('health.passed') : t('health.failed')) : t('health.checking')}
                    </p>
                    {full && (
                      <p className="text-xs" style={{ color: '#6B6A75' }}>
                        {t('health.latency', { ms: full.latencyMs })}
                      </p>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            {checkedAt && (
              <p className="text-xs" style={{ color: '#6B6A75' }}>
                {t('health.checked_at', { time: new Date(checkedAt).toLocaleTimeString() })}
              </p>
            )}
            <button
              type="button"
              onClick={() => void run()}
              disabled={running}
              className="public-btn-ghost disabled:cursor-not-allowed disabled:opacity-50"
              style={{ minHeight: '2.75rem', padding: '0 1rem' }}
            >
              {t('health.rerun')}
            </button>
          </div>

          <details className="mt-4">
            <summary className="cursor-pointer text-xs" style={{ color: '#8E8CB3' }}>
              JSON
            </summary>
            <pre
              className="mt-2 overflow-x-auto rounded-md p-3 text-xs"
              style={{ background: '#efeff5', color: '#1F1E26' }}
            >
              {JSON.stringify(report, null, 2)}
            </pre>
          </details>
        </div>
      </main>
    </PublicSiteFrame>
  );
}
